'use client'

import { useEffect, useState } from 'react'
import { Location, ProductOpeningHoursResponse, Region } from '@leid/api-locations'
import { Manager } from '@leid/api-managers'
import { GetPriceResponse, ProductAvailabilityInfo } from '@leid/api-orders'
import { useFormik } from 'formik'

import { CustomerInfo } from 'app/utils/authentication'
import { buildDate } from 'app/utils/dates'
import { GetPriceOptions, ItemApi } from 'appTypes/items'
import { SortingInfo } from 'appTypes/mapper'
import { OrderInfo, OrderItemInfo } from 'appTypes/orders'
import apiClient from 'helpers/clients/apiClient'

import RentalSectionClient from '../ProductInfoAndRentalSection/components/RentalSection/RentalSectionClient'

import { useCheckIfRerent } from './RentProductLogic/hooks/useCheckIfRerent'
import { useCheckIfCorrectPeriod } from './RentProductLogic/hooks/useCorrectPeriod'
import { useHandleAmountChange } from './RentProductLogic/hooks/useHandleAmountChange'
import { useHandlePickupChange } from './RentProductLogic/hooks/useHandlePickupChange'
import { useHandleRegionLocationChange } from './RentProductLogic/hooks/useHandleRegionLocationChange'
import { useHandleReturnChange } from './RentProductLogic/hooks/useHandleReturnChange'
import { useIsRentalBeyondMaxTime } from './RentProductLogic/hooks/useIsRentalBeyondMaxTime'
import { useRentProductLogicFormikOptions } from './RentProductLogic/hooks/useRentProductLogicFormikOptions'
import { useShouldDisablePickupDate } from './RentProductLogic/hooks/useShouldDisablePickupDate'
import { useShouldDisablePickupHour } from './RentProductLogic/hooks/useShouldDisablePickupHour'
import { useShouldDisableReturnDate } from './RentProductLogic/hooks/useShouldDisableReturnDate'
import { useShouldDisableReturnHour } from './RentProductLogic/hooks/useShouldDisableReturnHour'
import { RentProductLoginSubmitHandler } from './RentProductLogic/interfaces'

type Props = {
  product: ItemApi
  orderInfo: OrderInfo | undefined
  orderItemInfo: OrderItemInfo | undefined
  productCalendarList: ProductAvailabilityInfo
  productLocations: Location[]
  customerInfo: CustomerInfo
  openingHours: ProductOpeningHoursResponse[]
  isEditMode: boolean
  isProlonging: boolean
  onSubmit: RentProductLoginSubmitHandler
  rentedFrom: string
  rentedTo: string
  isDeductibles: boolean
  isInsurance: boolean
  locationId: number | undefined
  regionId: number | undefined
  managerId: number | undefined
  amount: number | undefined
  step: number | undefined
  isRerent: boolean
  regions: Region[]
  manager: Manager | undefined
  sortingInfo: SortingInfo | undefined
}

const RentProduct = (props: Props): JSX.Element => {
  const {
    product,
    orderInfo,
    orderItemInfo,
    productCalendarList,
    openingHours,
    productLocations,
    customerInfo,
    onSubmit,
    isEditMode,
    isProlonging,
    rentedFrom,
    rentedTo,
    isDeductibles,
    isInsurance,
    locationId,
    regionId,
    managerId,
    amount,
    step,
    isRerent,
    regions,
    manager,
    sortingInfo,
  } = props

  const [productPrice, setProductPrice] = useState<GetPriceResponse | null>(null)

  const formikOptions = useRentProductLogicFormikOptions({
    onSubmit,
    amount,
    isDeductibles,
    isInsurance,
    isProlonging,
    locationId,
    managerId,
    regionId,
    rentedFrom,
    rentedTo,
  })
  const { initialValues } = formikOptions
  const formikConfig = useFormik(formikOptions)
  const { values, touched } = formikConfig

  useEffect(() => {
    // when editing fetch price immediately, but other than that only after return time was changed
    if (isEditMode || touched.returnAt) {
      getPrice()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, values, touched])

  const getPriceWhenBooking = async () => {
    if (
      values.pickupAt &&
      values.returnAt &&
      values.startDate &&
      values.endDate &&
      values.includeInsurance !== undefined &&
      values.includeDeductibles !== undefined
    ) {
      const rentedFrom = buildDate(values.startDate, values.pickupAt)
      const rentedTo = buildDate(values.endDate, values.returnAt)

      const options: GetPriceOptions = {
        rentedFrom,
        rentedTo,
        includeCleaning: false,
        includeInsurance: values.includeInsurance,
        includeDeductibles: values.includeDeductibles,
        locationId: values.selectedLocationId,
        amount: values.amount,
        organizationId: customerInfo.organization?.id,
      }

      try {
        const priceResponse = await apiClient.getProductPrice(product.id, options)
        if (!priceResponse.success) {
          return
        }

        const price = priceResponse.data
        if (price !== undefined && price !== null) {
          setProductPrice(price)
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        // rollbar.error('Error in getPriceWhenBooking', err)
      }
    }
  }

  const getPriceWhenProlonging = async () => {
    if (values.returnAt && values.endDate) {
      const rentedTo = buildDate(values.endDate, values.returnAt)

      const orderItemId = orderItemInfo?.orderItem?.id ?? 0

      try {
        const priceResponse = await apiClient.getProlongationPrice(orderItemId, { rentedTo })
        if (!priceResponse.success) {
          // rollbar.error('Error in getPriceWhenProlonging', priceResponse.error)
          return
        }

        const price = priceResponse.data
        if (price !== undefined && price !== null) {
          setProductPrice(price)
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        // rollbar.error('Error in getPriceWhenProlonging', err)
      }
    }
  }

  const getPrice = async () => {
    if (isProlonging) {
      getPriceWhenProlonging()
    } else {
      getPriceWhenBooking()
    }
  }

  const isRentalBeyondMaxTime = useIsRentalBeyondMaxTime(product, values, undefined, rentedFrom, isProlonging)

  const handlePickupChange = useHandlePickupChange(formikConfig)
  const handleReturnChange = useHandleReturnChange(formikConfig)
  const handleRegionLocationChange = useHandleRegionLocationChange(formikConfig)
  const handleAmountChange = useHandleAmountChange(formikConfig)
  const checkIfCorrectPeriod = useCheckIfCorrectPeriod(values, orderInfo, productCalendarList, openingHours)
  const checkIfRerent = useCheckIfRerent(values, productCalendarList)
  const shouldDisablePickupDate = useShouldDisablePickupDate(isProlonging, values, productCalendarList, openingHours)
  const shouldDisablePickupHour = useShouldDisablePickupHour(isProlonging, values, productCalendarList, openingHours)
  const shouldDisableReturnDate = useShouldDisableReturnDate(
    values,
    initialValues,
    isProlonging,
    orderInfo,
    productCalendarList,
    openingHours,
  )
  const shouldDisableReturnHour = useShouldDisableReturnHour(
    values,
    initialValues,
    isProlonging,
    orderInfo,
    productCalendarList,
    openingHours,
  )

  return (
    <RentalSectionClient
      orderItemInfo={orderItemInfo}
      isProlonging={isProlonging}
      step={step}
      isRerent={isRerent}
      product={product}
      productLocations={productLocations}
      productPrice={productPrice}
      handlePickupChange={handlePickupChange}
      handleReturnChange={handleReturnChange}
      handleRegionLocationChange={handleRegionLocationChange}
      handleAmountChange={handleAmountChange}
      checkIfCorrectPeriod={checkIfCorrectPeriod}
      checkIfRerent={checkIfRerent}
      shouldDisablePickupDate={shouldDisablePickupDate}
      shouldDisablePickupHour={shouldDisablePickupHour}
      shouldDisableReturnDate={shouldDisableReturnDate}
      shouldDisableReturnHour={shouldDisableReturnHour}
      customerInfo={customerInfo}
      productCalendarList={productCalendarList}
      regions={regions}
      manager={manager}
      isEditMode={isEditMode}
      isRentalBeyondMaxTime={isRentalBeyondMaxTime}
      isSubmitting={formikConfig.isSubmitting}
      touched={formikConfig.touched}
      setFieldValue={formikConfig.setFieldValue}
      setTouched={formikConfig.setTouched}
      setValues={formikConfig.setValues}
      submitForm={formikConfig.submitForm}
      values={formikConfig.values}
      sortingInfo={sortingInfo}
    />
  )
}

export default RentProduct
